import React, { useRef } from 'react';
import {
  GoogleMap,
  Libraries,
  Marker,
  useJsApiLoader
} from '@react-google-maps/api';
import { useDebounce } from 'use-debounce';
import iconMarkerGreen from '../../assets/map/map_pointer_green.svg';
import iconMarkerBlue from '../../assets/map/map_pointer_blue.svg';
import iconMarkerGray from '../../assets/map/map_pointer_gray.svg';
import iconMarkerGreenRoaming from '../../assets/map/map_pointer_green_roaming.svg';
import iconMarkerBlueRoaming from '../../assets/map/map_pointer_blue_roaming.svg';
import iconMarkerGrayRoaming from '../../assets/map/map_pointer_gray_roaming.svg';
import iconMarkerOrange from '../../assets/map/map_pointer_orange.svg';
import { AVAILABILITY_NOT_INFORMED, dataSourceOf, isInformationalSite } from '../../utils/sitePinType';
import imageRoaming from '../../assets/world_roaming_icon.svg';
import imageCenterMap from '../../assets/map/new_maps_center_icon.svg';
import iconMarkerUser from '../../assets/map/map_pointer_user.svg';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useLocationContext,
  loadSavedView,
  saveView
} from '../../context/LocationContext';
import { useBrandContext } from '../../context/BrandContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../modal/Modal';
import './Map.css';
import clock from '../../assets/clock.svg';
import helpIcon from '../../assets/helpIcon.svg';
import pin from '../../assets/pin_drop.svg';
import place from '../../assets/place.svg';
import checkCircleOutiline from '../../assets/check_circle_outline.svg';
import imageThunderBlue from '../../assets/thunder_blue.svg';
import imageThunderGreen from '../../assets/thunder_green.svg';
import imageThunderWhite from '../../assets/thunder_white.svg';
import Button from '../button/Button';
import { formatKm } from '../../utils/format';
import { useToastStore } from '../../hooks/useToastStore';
import ToastMapsOptions from '../toastMapsOptions/ToastMapsOptions';
import ToastHelp from '../toastHelp/ToastHelp';
import { useMapViewStore } from '../../hooks/useMapViewStore';
import '../chargerStationDetails/ChargerStationDetails.css';

const containerStyle = {
  width: '100%',
  height: '100%'
};

type MapProps = {
  chargerSites: any[];
  isLoadingPins: boolean;
  style: any;
  showMap: boolean;
  isSiteDetailModalOpen: boolean;
  setIsSiteDetailModalOpen: (value: boolean) => void;
  isActiveRechargesModalOpen: boolean;
  setIsActiveRechargesModalOpen: (value: boolean) => void;
};

const libraries: Libraries = ['geometry'];

const Map: React.FC<MapProps> = (mapProps: any) => {
  const locationContext = useLocationContext();
  const navigate = useNavigate();
  const [newMapCenter, setNewMapCenter] = useState<google.maps.LatLng | null>();
  const [debouncedMapCenter] = useDebounce(newMapCenter, 500);
  const center = locationContext.userLocation;
  const {
    isSiteDetailModalOpen,
    setIsSiteDetailModalOpen,
    isActiveRechargesModalOpen,
    setIsActiveRechargesModalOpen,
    showMap
  } = mapProps;
  const [activeCs, setActiveCs] = useState<any>();
  const brandContext = useBrandContext();
  const {
    setIsCsDetailActive,
    headerHeight,
    triggerAddressSearch,
    hasNewSearch,
    setHasNewSearch,
    map,
    setMap
  } = useMapViewStore();

  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${activeCs?.lat},${activeCs?.long}`;
  const appleURl = `http://maps.apple.com/?q=${activeCs?.lat},${activeCs?.long}`;
  const wazeUrl = `https://waze.com/ul?ll=${activeCs?.lat},${activeCs?.long}&navigate=yes`;

  const {
    showHelpToast,
    showDirectionsToast,
    setShowHelpToast,
    setShowDirectionsToast
  } = useToastStore();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
    mapIds: [process.env.REACT_APP_GOOGLE_MAPS_CUSTOM_MAP_ID!],
    libraries: libraries
  });

  const mapOptions = {
    streetViewControl: false,
    scaleControl: false,
    mapTypeControl: false,
    panControl: false,
    zoomControl: false,
    rotateControl: false,
    fullscreenControl: false,
    styles: [
      {
        featureType: 'poi',
        elementType: 'all',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'transit',
        elementType: 'all',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'road',
        elementType: 'all',
        stylers: [{ visibility: 'on' }]
      },
      {
        featureType: 'landscape',
        elementType: 'all',
        stylers: [{ visibility: 'on' }]
      },
      {
        featureType: 'administrative',
        elementType: 'all',
        stylers: [{ visibility: 'on' }]
      }
    ]
  };

  let total_status_icons = 6;

  const icon_available_count =
    activeCs?.chargerSiteStatus?.available > total_status_icons
      ? total_status_icons
      : activeCs?.chargerSiteStatus?.available;
  total_status_icons =
    total_status_icons - icon_available_count < 0
      ? 0
      : total_status_icons - icon_available_count;

  const icon_inUse_count =
    activeCs?.chargerSiteStatus?.inUse > total_status_icons
      ? total_status_icons
      : activeCs?.chargerSiteStatus?.inUse;
  total_status_icons =
    total_status_icons - icon_inUse_count < 0
      ? 0
      : total_status_icons - icon_inUse_count;

  const icon_offLine_count =
    activeCs?.chargerSiteStatus?.offLine > total_status_icons
      ? total_status_icons
      : activeCs?.chargerSiteStatus?.offLine;

  useEffect(() => {
    if (
      isLoaded &&
      map &&
      locationContext.userLocation &&
      hasNewSearch !== false &&
      showMap &&
      mapProps.chargerSites.length > 0
    ) {
      setHasNewSearch(false);

      let closestSite: google.maps.LatLng | null = null;
      let minDistance = Infinity;

      let referenceLatLng: google.maps.LatLng;

      if (locationContext.searchLocation) {
        // só entra aqui se searchLocation não for null/undefined
        referenceLatLng = new google.maps.LatLng(
          locationContext.searchLocation.lat,
          locationContext.searchLocation.lng
        );
      } else {
        // fallback: se searchLocation não existir, usa a posição do usuário mesmo
        referenceLatLng = new google.maps.LatLng(
          locationContext.userLocation.lat,
          locationContext.userLocation.lng
        );
      }

      mapProps.chargerSites.forEach((site: any) => {
        const siteLatLng = new google.maps.LatLng(
          site.csLocation.coordinates[1],
          site.csLocation.coordinates[0]
        );
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
          referenceLatLng,
          siteLatLng
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestSite = siteLatLng;
        }
      });

      if (closestSite) {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(referenceLatLng);
        bounds.extend(closestSite);
        map.fitBounds(bounds);
      }
    }
  }, [
    isLoaded,
    map,
    locationContext.userLocation,
    mapProps.chargerSites,
    triggerAddressSearch
  ]);

  // Card aberto acompanha a atualização automática de status dos pins
  useEffect(() => {
    if (!activeCs) return;
    const current = mapProps.chargerSites.find(
      (site: any) => site.csId === activeCs.csId
    );
    if (current && current !== activeCs) setActiveCs(current);
  }, [mapProps.chargerSites]);

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    setMap(map);
    const savedView: any = loadSavedView();
    if (savedView?.center) {
      map.panTo({ lat: savedView?.center.lat, lng: savedView?.center.lng });
      map.setZoom(savedView?.zoom);
    }
  }, []);

  const getMarkerIcon = (site: any) => {
    // Local apenas informativo: laranja, sem cor de status (o status vindo da base externa não é confiável)
    if (isInformationalSite(site)) return iconMarkerOrange;
    if (site.chargerSiteStatus?.available > 0) {
      return site.isOcpi ? iconMarkerGreenRoaming : iconMarkerGreen;
    } else if (site.chargerSiteStatus?.inUse > 0) {
      return site.isOcpi ? iconMarkerBlueRoaming : iconMarkerBlue;
    } else {
      return site.isOcpi ? iconMarkerGrayRoaming : iconMarkerGray;
    }
  };

  const getMarkerUserIcon = () => {
    return iconMarkerUser;
  };

  const navigateToCharger = (site: any) => {
    navigate(`/${brandContext.brandName}/charger/${site.csId}`, {
      state: { chargerSiteInfos: site }
    });
  };

  const chargerStationPreview = (site: any) => {
    setActiveCs(site);
    setTimeout(
      () => {
        setIsSiteDetailModalOpen(true);
      },
      isActiveRechargesModalOpen ? 250 : 0
    );
    setIsActiveRechargesModalOpen(false);
    setIsCsDetailActive(true);
  };

  const updateLocationSearch = () => {
    if (!map) return;
    const bounds = map.getBounds();
    const center = map.getCenter();

    if (!bounds || !center || !locationContext.searchLocation) return;

    const newLocation = { lat: center.lat(), lng: center.lng() };

    const distanceMoved = locationContext.getDistance(
      { from: locationContext.searchLocation, to: newLocation },
      'm',
      0
    );

    if (distanceMoved === 0 || distanceMoved < 5000) {
      return;
    }

    const radius =
      locationContext.getDistance(
        {
          from: {
            lat: bounds.getNorthEast().lat(),
            lng: bounds.getNorthEast().lng()
          },
          to: {
            lat: bounds.getSouthWest().lat(),
            lng: bounds.getSouthWest().lng()
          }
        },
        'm',
        0
      ) / 2;
    locationContext.setSearchRadius(radius);

    locationContext.setSearchLocation(newLocation);
  };

  useEffect(() => {
    if (debouncedMapCenter && isLoaded && map) {
      updateLocationSearch();
    }
  }, [debouncedMapCenter]);

  useEffect(() => {
    if (!map || !isLoaded) return;
    const loc = locationContext.searchLocation;
    if (!loc) return;

    const current = map.getCenter();
    const sameCenter =
      current &&
      Math.abs(current.lat() - loc.lat) < 1e-6 &&
      Math.abs(current.lng() - loc.lng) < 1e-6;

    // centraliza apenas se mudou de fato
    if (!sameCenter) {
      map.panTo({ lat: loc.lat, lng: loc.lng });
    }
  }, [locationContext.searchLocation]);

  function handleCenterChanged() {
    if (map && center) {
      setTimeout(() => {
        const _center: any = map.getCenter();
        const bounds: any = map.getBounds();
        const zoom: any = map.getZoom();
        setNewMapCenter(_center);
        saveView({
          center: { lat: _center.lat(), lng: _center.lng() },
          bounds,
          zoom
        });
      }, 1000);
    }
  }

  return isLoaded ? (
    <div
      style={{
        ...{
          position: 'absolute',
          top: `${headerHeight - 20}px`,
          height: `calc(100vh - ${headerHeight - 20}px)`
        },
        ...(mapProps.style ? mapProps.style : {})
      }}
    >
      <div className="map-container">
        <GoogleMap
          mapContainerStyle={containerStyle}
          onLoad={onLoad}
          options={mapOptions}
          onCenterChanged={handleCenterChanged}
          zoom={15}
        >
          <Marker
            key="user-key"
            icon={getMarkerUserIcon()}
            position={locationContext.userLocation}
            zIndex={1}
          />
          {mapProps.chargerSites.map((site: any) => (
            <Marker
              key={site.csId}
              icon={getMarkerIcon(site)}
              position={{
                lat: site.csLocation.coordinates[1],
                lng: site.csLocation.coordinates[0]
              }}
              onClick={() => chargerStationPreview(site)}
              zIndex={2}
            />
          ))}
        </GoogleMap>
      </div>
      <Modal
        allowButtonClosed={false}
        isOpen={isSiteDetailModalOpen}
        onClose={() => {
          setIsSiteDetailModalOpen(false);
          setIsCsDetailActive(false);
        }}
        // Local apenas informativo não tem tela de detalhes: sem texto o Modal não desenha o botão
        textEnterButton={isInformationalSite(activeCs) ? '' : 'Ver mais'}
        handleEnterButton={() => navigateToCharger(activeCs)}
        isLoadingEnterButton={false}
        styleEnterButton={{ backgroundColor: `${brandContext.brandBgColor}` }}
        disableEnterButton={false}
        modalBackdrop={false}
        header={false}
      >
        {activeCs?.csName && (
          <div className="charger-station-details" style={{ padding: 0 }}>
            <h1 className="charger-station-name">
              {activeCs.csName}{' '}
              {activeCs.isOcpi && (
                <img
                  className="world-roaming-icon"
                  loading="lazy"
                  alt="roaming-icon"
                  src={imageRoaming}
                />
              )}
            </h1>

            {/* Endereço */}
            <div className="charger-station-detail-lines">
              <img
                src={place}
                className={'charger-icon charger-icon-blue'}
                alt="Local"
              />
              <span className="charger-station-detail-lines_text charger-station-address">
                <b>{formatKm(activeCs.distanceMeters / 1000)} km </b> -{' '}
                {activeCs.csFormattedAddress}
              </span>
            </div>

            {/* Estacionamento — a base externa do local informativo não traz: não mostra */}
            {!isInformationalSite(activeCs) && (
              <div className="charger-station-detail-lines">
                <img
                  src={checkCircleOutiline}
                  className={'charger-icon'}
                  alt="Estacionamento"
                />
                <span className="charger-station-detail-lines_text charger-station-parking-info">
                  {activeCs.csFreeParking
                    ? 'Estacionamento: gratuito'
                    : 'Estacionamento: pago'}
                </span>
              </div>
            )}

            {/* Horário — no local informativo não há horário de funcionamento: só público/privado */}
            <div className="charger-station-detail-lines">
              <img
                src={clock}
                className={'charger-icon charger-icon-blue'}
                alt="Horário"
              />
              <span className="charger-station-detail-lines_text charger-station-operation-hours">
                {isInformationalSite(activeCs)
                  ? activeCs?.csPrivate ? 'Privado' : 'Público'
                  : `${activeCs?.csPrivate ? 'Privado / ' : 'Público / '} ${
                      activeCs.isOpen ? 'Aberto' : 'Fechado'
                    }`}
              </span>
            </div>

            {/* Status dos carregadores */}
            <div
              className="charger-card-chargers"
              style={{ justifyContent: 'flex-start', marginTop: '16px' }}
            >
              {isInformationalSite(activeCs) ? (
                <span className="charger-station-detail-lines_text">
                  {AVAILABILITY_NOT_INFORMED}
                  {dataSourceOf(activeCs) && (
                    <>
                      <br />
                      <small>{dataSourceOf(activeCs)}</small>
                    </>
                  )}
                </span>
              ) : (
              <span className="charger-card-chargers_thunder">
                {Array.from({ length: icon_available_count }).map(
                  (_, index) => (
                    <img
                      key={`available-${index}`}
                      src={imageThunderGreen}
                      className={'charger-icon charger-icon-green'}
                      alt="Disponível"
                      width={10}
                      style={{ marginRight: '5px' }}
                    />
                  )
                )}
                {Array.from({ length: icon_inUse_count }).map((_, index) => (
                  <img
                    key={`inUse-${index}`}
                    src={imageThunderBlue}
                    className={'charger-icon charger-icon-blue'}
                    alt="Em uso"
                    width={10}
                    style={{ marginRight: '5px' }}
                  />
                ))}
                {Array.from({ length: icon_offLine_count }).map((_, index) => (
                  <img
                    key={`offLine-${index}`}
                    src={imageThunderWhite}
                    className={'charger-icon charger-icon-white'}
                    alt="Não disponível"
                    width={10}
                    style={{ marginRight: '5px' }}
                  />
                ))}
              </span>
              )}
            </div>

            {/* Ações */}
            <div className="actions-container" style={{ marginBottom: '5px' }}>
              <Button
                className="btn-outline-md"
                type="button"
                children="Como Chegar"
                spinnerSecondary={true}
                onClick={() => setShowDirectionsToast(true)}
                image={<img src={pin} alt="Como Chegar" />}
                style={{ fontSize: 15 }}
              />

              {!isInformationalSite(activeCs) && (
                <Button
                  className="btn-outline-md"
                  type="button"
                  children="Ajuda"
                  spinnerSecondary={true}
                  onClick={() => setShowHelpToast(true)}
                  image={<img src={helpIcon} alt="Ajuda" />}
                  style={{ fontSize: 15 }}
                />
              )}
            </div>
          </div>
        )}
      </Modal>
      <ToastMapsOptions
        googleMapsUrl={mapUrl}
        appleMapsUrl={appleURl}
        wazeUrl={wazeUrl}
        isModalOpen={showDirectionsToast}
        allowButtonClosed={true}
        setShowToastMapsOptions={setShowDirectionsToast}
      />
      <ToastHelp
        isModalOpen={showHelpToast}
        allowButtonClosed={true}
        setShowHelpToast={setShowHelpToast}
      />
    </div>
  ) : (
    <></>
  );
};

export default Map;
